import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { Moment } from "moment";
import RangeCalendar, { type Range } from "./RangeCalendar";

const DEFAULT_FORMAT = "DD/MM/YYYY";

export interface DateRangePickerProps {
  /** Current range as ISO date strings (empty string = no date). */
  value?: { from: string; to: string };
  /** Called when user applies the selected range. Receives ISO date strings. */
  onChange?: (from: string, to: string) => void;
  /** Label for the input (e.g. "Date range"). */
  label?: string;
  /** Placeholder when no range is selected. */
  placeholder?: string;
  /** Display format for dates (moment format). */
  format?: string;
  /** Size of the input. */
  size?: "small" | "medium";
  /** Disabled state. */
  disabled?: boolean;
  /** Minimum width of the text field. */
  minWidth?: number;
}

/**
 * A single input that opens a dialog with a two-month range calendar.
 * User selects start and end in the calendar, then applies with "Apply" or cancels.
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value = { from: "", to: "" },
  onChange,
  label = "Date range",
  placeholder = "Select date range",
  format = DEFAULT_FORMAT,
  size = "small",
  disabled = false,
  minWidth = 220,
}) => {
  const [open, setOpen] = useState(false);
  const [dialogRange, setDialogRange] = useState<Range>({
    start: null,
    end: null,
  });

  const displayText = useMemo(() => {
    if (!value.from && !value.to) return "";
    const fromStr = value.from
      ? moment.utc(value.from).local().format(format)
      : "…";
    const toStr = value.to ? moment.utc(value.to).local().format(format) : "…";
    return `${fromStr} - ${toStr}`;
  }, [value.from, value.to, format]);

  const handleOpen = useCallback(() => {
    setDialogRange({
      start: value.from ? moment.utc(value.from).local() : null,
      end: value.to ? moment.utc(value.to).local() : null,
    });
    setOpen(true);
  }, [value.from, value.to]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleDialogRangeChange = useCallback(
    (start: Moment | null, end: Moment | null) => {
      setDialogRange({ start, end });
    },
    [],
  );

  const handleApply = useCallback(() => {
    const from = dialogRange.start
      ? dialogRange.start.startOf("day").toISOString()
      : "";
    const to = dialogRange.end
      ? dialogRange.end.endOf("day").toISOString()
      : "";
    onChange?.(from, to);
    setOpen(false);
  }, [dialogRange, onChange]);

  const handleClear = useCallback(() => {
    setDialogRange({ start: null, end: null });
    onChange?.("", "");
    setOpen(false);
  }, [onChange]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <TextField
        label={label}
        value={displayText}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        onClick={handleOpen}
        sx={{
          minWidth,
          "& .MuiInputBase-input": { cursor: disabled ? "default" : "pointer" },
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        inputProps={{ "aria-label": label }}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select date range</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <RangeCalendar
              value={dialogRange}
              onChange={handleDialogRangeChange}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClear} color="inherit">
            Clear
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default React.memo(DateRangePicker);
